import { useState, useEffect, useMemo, useRef } from 'react';
import { adminApi } from '../../utils/api';
import {
  PHASE_TREE,
  SHAPE,
  summariseSelection,
  requiredCapabilities,
  selectionTotals,
  coverageIndex,
} from './gameCreator/model';
import { buildBrief, buildDeploySteps, buildDeployScript, gameSlug } from './gameCreator/brief';

/**
 * AdminGameCreator - the Claude Code Game Creator.
 *
 * Produces two things and runs neither: the brief a person pastes into a fresh
 * Claude Code session to build a game, and the terminal commands they run
 * afterwards to deploy it. There is no "generate" button and nothing to submit;
 * the text follows the form as it is filled in.
 *
 * Everything in the coverage list comes from engine/framework.js through
 * gameCreator/model.js, so the ten phases and fifty-three levels here cannot
 * drift from the ones the engine scores against. The wording under each level
 * is chart/parentLanguage.js's - the same words a parent reads on the chart.
 *
 * Admin only, by the guard on /admin/* in App.jsx. Nothing here reads or shows
 * a child's record.
 */
export default function AdminGameCreator() {
  const [letClaudeName, setLetClaudeName] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [setting, setSetting] = useState('');
  const [vocabulary, setVocabulary] = useState('');

  const [selected, setSelected] = useState({});
  const [sketches, setSketches] = useState({});
  const [expanded, setExpanded] = useState({});

  const [games, setGames] = useState(null);
  const [gamesError, setGamesError] = useState(null);

  useEffect(() => {
    adminApi.getGames()
      .then(data => setGames(data.games || []))
      .catch(() => setGamesError('Could not read the games registry, so this page cannot '
        + 'say what ground is already taken.'));
  }, []);

  const covered = useMemo(() => coverageIndex(games || []), [games]);
  const totals = useMemo(() => selectionTotals(selected), [selected]);
  const caps = useMemo(() => requiredCapabilities(selected), [selected]);
  const chosen = useMemo(() => summariseSelection(selected), [selected]);

  const spec = useMemo(() => ({
    name, letClaudeName, description, setting, vocabulary, selected, sketches,
    // Numbers the game doc: GAME-03 if two games are registered.
    existingGameCount: games ? games.length : null,
  }), [name, letClaudeName, description, setting, vocabulary, selected, sketches, games]);

  const brief = useMemo(() => buildBrief(spec), [spec]);
  const steps = useMemo(() => buildDeploySteps(spec), [spec]);

  const toggleLevel = (key) => setSelected(prev => {
    const next = { ...prev };
    if (next[key]) delete next[key]; else next[key] = true;
    return next;
  });

  const toggleWholePhase = (phase) => {
    const keys = phase.levels.map(l => l.key);
    const allOn = keys.every(k => selected[k]);
    setSelected(prev => {
      const next = { ...prev };
      for (const key of keys) {
        if (allOn) delete next[key]; else next[key] = true;
      }
      return next;
    });
    if (!allOn) setExpanded(prev => ({ ...prev, [phase.phase]: true }));
  };

  const clearAll = () => { setSelected({}); setSketches({}); };

  return (
    <div className="max-w-5xl">
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
        Claude Code Game Creator
      </h1>
      <p className="text-sm text-gray-600 mb-6">
        Describe the game, tick the coverage, paste the brief into a fresh Claude Code
        session in <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">/var/www/speechformation</code>.
        This page runs nothing. It produces text.
      </p>

      {/* ---------------------------------------------------------------- 1 */}
      <Panel step="1" title="The idea">
        <div className="space-y-4">
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <label className="block text-sm font-medium text-gray-700">Game name</label>
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  className="rounded border-gray-300"
                  checked={letClaudeName}
                  onChange={e => setLetClaudeName(e.target.checked)}
                />
                Let Claude choose
              </label>
            </div>
            <input
              type="text"
              value={letClaudeName ? '' : name}
              disabled={letClaudeName}
              onChange={e => setName(e.target.value)}
              placeholder={letClaudeName ? 'Claude will pick a name and an id' : 'Post Office'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
            />
            <p className="text-xs text-gray-500 mt-1">
              {letClaudeName
                ? 'Every path in the brief and every command below says <game-id> until a name exists. games.id is a foreign key from every attempt, so it is not guessed here.'
                : <>Folder and id: <code className="bg-gray-100 px-1 rounded">{gameSlug(spec)}</code></>}
            </p>
          </div>

          <Field
            label="What the game is"
            hint="A few sentences. What the child sees, what they say, what happens when they say it."
            value={description}
            onChange={setDescription}
            rows={4}
            placeholder="Parcels arrive at a counter. The child says what is in one and it opens."
          />
          <Field
            label="Setting and characters"
            hint="Optional."
            value={setting}
            onChange={setSetting}
            rows={2}
          />
          <Field
            label="Vocabulary or theme"
            hint="Optional. Eight words to a pool, not four — and nothing with an obscene near-miss (CLAUDE.md §5)."
            value={vocabulary}
            onChange={setVocabulary}
            rows={2}
          />
        </div>
      </Panel>

      {/* ---------------------------------------------------------------- 2 */}
      <Panel
        step="2"
        title="Coverage"
        aside={
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500">
              {totals.levels} of {SHAPE.levels} levels · {totals.boxes} boxes
            </span>
            {totals.levels > 0 && (
              <button onClick={clearAll} className="text-xs text-gray-500 hover:text-gray-900 underline">
                clear
              </button>
            )}
          </div>
        }
      >
        <p className="text-xs text-gray-500 mb-4">
          {SHAPE.phases} phases, {SHAPE.levels} levels, {SHAPE.boxes} boxes, read from
          the engine. A game covers a slice — Door Knock covers eight levels.
        </p>

        {gamesError && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            {gamesError}
          </p>
        )}
        {covered.problems.map(p => (
          <p key={p.game} className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <strong>{p.game}</strong> declares coverage the engine rejects, so it is left
            out of the badges below: {p.errors[0]}
          </p>
        ))}

        <div className="space-y-3">
          {PHASE_TREE.map(phase => (
            <PhaseCard
              key={phase.phase}
              phase={phase}
              selected={selected}
              sketches={sketches}
              coveredBy={covered.byPhase[phase.phase]}
              coveredLevels={covered.byLevel}
              open={Boolean(expanded[phase.phase])}
              onToggleOpen={() => setExpanded(prev => ({ ...prev, [phase.phase]: !prev[phase.phase] }))}
              onToggleAll={() => toggleWholePhase(phase)}
              onToggleLevel={toggleLevel}
              onSketch={(key, text) => setSketches(prev => ({ ...prev, [key]: text }))}
            />
          ))}
        </div>

        {caps.length > 0 && (
          <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <h3 className="text-sm font-semibold text-blue-900 mb-1">
              This coverage needs {caps.map(c => c.id).join(' and ')}
            </h3>
            {caps.map(cap => (
              <p key={cap.id} className="text-xs text-blue-900/80 mt-1">
                <strong>{cap.id}</strong> — {cap.boxes} of the {totals.boxes} boxes ticked
                (phase{cap.phases.length > 1 ? 's' : ''} {cap.phases.join(', ')}). {cap.detail}
              </p>
            ))}
          </div>
        )}
      </Panel>

      {/* ---------------------------------------------------------------- 3 */}
      <Panel
        step="3"
        title="The brief"
        aside={<CopyButton text={brief} label="Copy brief" />}
      >
        <p className="text-xs text-gray-500 mb-3">
          Paste into a new session. It stays short on purpose — the session reads CLAUDE.md
          itself, and a second copy of the rules here would be the one that goes stale.
        </p>
        <pre className="text-[11px] sm:text-xs leading-relaxed text-gray-800 bg-gray-50 border border-gray-200 rounded-lg p-4 overflow-x-auto whitespace-pre">
{brief}
        </pre>
      </Panel>

      <Panel
        step="4"
        title="Deploy, afterwards"
        aside={<CopyButton text={buildDeployScript(spec)} label="Copy all" subtle />}
      >
        <p className="text-xs text-gray-500 mb-4">
          Run one at a time, in order, checking each before the next. Paths and the pm2
          process name come from README.md and <code className="bg-gray-100 px-1 rounded">server/.env</code> —
          the only thing on this page not read from code, so check them. Steps 4 to 8 are
          the ones the Claude session is forbidden to run.
        </p>
        <ol className="space-y-3">
          {steps.map((step, i) => (
            <li key={step.id} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="flex items-start justify-between gap-3 px-4 py-3 bg-gray-50">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    <span className="text-gray-400 mr-2">{i + 1}</span>{step.title}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{step.note}</p>
                </div>
                <CopyButton text={step.command} label="Copy" subtle />
              </div>
              <pre className="text-[11px] sm:text-xs text-gray-800 px-4 py-3 overflow-x-auto whitespace-pre">
{step.command}
              </pre>
            </li>
          ))}
        </ol>
      </Panel>

      {chosen.length === 0 && (
        <p className="text-xs text-gray-500 mt-6">
          Nothing ticked yet, so the brief has no coverage in it.
        </p>
      )}
    </div>
  );
}

/* ========================================================================= */

function Panel({ step, title, aside, children }) {
  return (
    <section className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h2 className="font-display text-lg font-bold text-gray-900">
          <span className="text-gray-300 mr-2">{step}</span>{title}
        </h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

function Field({ label, hint, value, onChange, rows = 3, placeholder }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <textarea
        rows={rows}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
      />
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}

function PhaseCard({
  phase, selected, sketches, coveredBy, coveredLevels,
  open, onToggleOpen, onToggleAll, onToggleLevel, onSketch,
}) {
  const keys = phase.levels.map(l => l.key);
  const on = keys.filter(k => selected[k]).length;
  const all = on === keys.length;
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = on > 0 && !all;
  }, [on, all]);

  return (
    <div className={`border rounded-lg ${on ? 'border-primary-300 bg-primary-50/30' : 'border-gray-200'}`}>
      <div className="flex items-start gap-3 p-3 sm:p-4">
        <input
          ref={ref}
          type="checkbox"
          checked={all}
          onChange={onToggleAll}
          className="mt-1 rounded border-gray-300 shrink-0"
          aria-label={`Cover all of phase ${phase.phase}`}
        />
        <button
          type="button"
          onClick={onToggleOpen}
          className="flex-1 min-w-0 text-left"
        >
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-sm font-semibold text-gray-900">
              Phase {phase.phase} · {phase.name}
            </span>
            <span className="text-xs text-gray-500">
              {phase.levelCount} levels · {phase.boxCount} boxes
            </span>
          </div>
          <p className="text-xs text-gray-600 mt-0.5">{phase.plain}</p>
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {coveredBy
              ? <Tag tone="gray">covered by {coveredBy.join(', ')}</Tag>
              : <Tag tone="green">no game covers this</Tag>}
            {phase.requires.map(id => <Tag key={id} tone="blue">needs {id}</Tag>)}
            {phase.observationalCount > 0 && (
              <Tag tone="gray">{phase.observationalCount} observational</Tag>
            )}
            {on > 0 && <Tag tone="primary">{on} of {phase.levelCount} ticked</Tag>}
          </div>
        </button>
        <span className="text-gray-400 text-xs mt-1 shrink-0">{open ? '▾' : '▸'}</span>
      </div>

      {open && (
        <div className="border-t border-gray-200 divide-y divide-gray-100">
          <p className="px-3 sm:px-4 py-2 text-[11px] text-gray-500">
            {phase.goal}. Child says: {phase.childSays}. Prompts: {phase.promptStyle.toLowerCase()}.
          </p>
          {phase.levels.map(level => (
            <LevelRow
              key={level.key}
              level={level}
              checked={Boolean(selected[level.key])}
              sketch={sketches[level.key] || ''}
              coveredBy={coveredLevels[level.key]}
              onToggle={() => onToggleLevel(level.key)}
              onSketch={text => onSketch(level.key, text)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function LevelRow({ level, checked, sketch, coveredBy, onToggle, onSketch }) {
  return (
    <div className="px-3 sm:px-4 py-3">
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="mt-1 rounded border-gray-300 shrink-0"
        />
        <div className="min-w-0 flex-1">
          <div className="text-sm text-gray-900">
            <span className="font-medium">Level {level.level}</span>
            <span className="text-gray-600"> — {level.intent}</span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {level.skills.map(s => s.short).join(' · ')}
          </p>
          {(coveredBy || level.requires.length > 0) && (
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {coveredBy && <Tag tone="gray">in {coveredBy.join(', ')}</Tag>}
              {level.requires.map(id => <Tag key={id} tone="blue">{id}</Tag>)}
            </div>
          )}
        </div>
      </label>

      {checked && (
        <textarea
          rows={2}
          value={sketch}
          onChange={e => onSketch(e.target.value)}
          placeholder={`Optional — what this level looks like in your game`}
          className="mt-2 ml-7 w-[calc(100%-1.75rem)] px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
        />
      )}
    </div>
  );
}

function Tag({ tone = 'gray', children }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-600',
    green: 'bg-green-100 text-green-800',
    blue: 'bg-blue-100 text-blue-800',
    primary: 'bg-primary-100 text-primary-800',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
}

/**
 * Copy, with the execCommand fallback. navigator.clipboard is unavailable over
 * plain HTTP, which is exactly how an admin panel gets looked at on a phone on
 * the local network, and a copy button that silently does nothing is worse than
 * no button.
 */
function CopyButton({ text, label = 'Copy', subtle = false }) {
  const [done, setDone] = useState(false);

  const copy = async () => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch {
      const area = document.createElement('textarea');
      area.value = text;
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      try { ok = document.execCommand('copy'); } catch { ok = false; }
      document.body.removeChild(area);
    }
    setDone(ok);
    setTimeout(() => setDone(false), 2000);
  };

  const base = 'shrink-0 rounded-lg transition-colors text-xs font-medium';
  const style = subtle
    ? 'px-3 py-1.5 border border-gray-300 text-gray-700 hover:bg-gray-100 bg-white'
    : 'px-4 py-2 bg-primary-500 text-white hover:bg-primary-600';

  return (
    <button type="button" onClick={copy} className={`${base} ${style}`}>
      {done ? 'Copied' : label}
    </button>
  );
}
